import { MainService } from 'app/main/MainService';
import { AppStateProvider } from 'app/shared/app_state/AppStateProvider';
import { EventRegistry } from 'app/shared/events/event_registry';
import { Selection } from 'app/shared/selection/Selection';
import { SelectionType } from 'app/shared/selection/SelectionType';
import * as _ from 'lodash';
import { KMLFeatureLayerCollection } from '../collections/kml_feature_layer_collection.service';
import { ImsObject } from 'app/shared/collections/ims_object';
import {
    getSelectedAssets,
    getSelectedConnectionEntities,
    getSelectedIPDevices,
    getSelectedKMLFeatureLayers,
    getSelectedRfTransmitters,
    getSelectedZones,
} from 'app/shared/selection/selection_utilities';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { ConnectionService } from 'app/views/view_types/network/shared/connections/connection.service';
import { ConnectionStat } from 'app/views/view_types/network/shared/connections/connectionStat';
import { KMLFeatureLayer } from 'app/shared/collections/kml_feature_layer';
import { HeatMapSelection } from 'app/shared/info_box/components/heatmap/heatmap';
import { parseSelectableFeatureId } from 'app/map/services/heatmap/AbstractGeoDataMap';
import { GeoDataService } from 'app/map/services/heatmap/GeoDataService';

export type OnSelectionChangedCallbackArgs = {
    selection: Array<Selection>;
    selected: Array<Selection>;
    deselected: Array<Selection>;
};

type ImsSelection = ImsObject | ConnectionStat | KMLFeatureLayer | HeatMapSelection;

export function matchSelection(selectionA, selectionB) {
    return selectionA.id === selectionB.id && selectionA.type === selectionB.type;
}

function diffSelection(selectionA, selectionB) {
    return selectionA.filter((x) => !selectionB.some((y) => matchSelection(x, y)));
}

function setSelectionFunc(selection) {
    return (state) => {
        if (state.frozen) {
            return state;
        }
        if (
            state.selection.length !== selection.length ||
            diffSelection(state.selection, selection).length
        ) {
            // TODO The diff function doesn't take into account order, meaning you can change selection order, it will be ignored
            return Object.assign({}, state, { selection });
        }
        return state;
    };
}

function filterAndSetSelectionsByType(type: SelectionType) {
    return (state) => {
        if (state.frozen) {
            return state;
        }
        const filteredSelection = state.selection.filter((s) => s.type !== type);
        const clonedState = _.cloneDeep(state);
        clonedState.selection = filteredSelection;
        return clonedState;
    };
}

export class SelectionService {
    private _state: AppStateProvider;
    private _cachedSelection: Selection[];
    private _onSelectEventRegistry: EventRegistry;
    private _onDeselectEventRegistry: EventRegistry;
    private _onFrozenEventRegistry: EventRegistry;
    private _onSelectionEventRegistry: EventRegistry;
    private _kmlFeatureLayerCollection: KMLFeatureLayerCollection;
    private _imsItemTypeRegistry: ImsItemTypeRegistry;
    private _geoDataService: GeoDataService;

    constructor(
        mainService: MainService,
        kmlFeatureLayerCollection: KMLFeatureLayerCollection,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        geoDataService?: GeoDataService,
    ) {
        function setFrozen(frozen) {
            return (state) => {
                return Object.assign({}, state, { frozen });
            };
        }

        this._kmlFeatureLayerCollection = kmlFeatureLayerCollection;
        this._state = new AppStateProvider({
            parent: mainService.state || undefined,
            keys: {
                selection: {
                    initialValue: [],
                },
                frozen: {
                    initialValue: false,
                },
            },
            effects: {
                setSelectionFunc,
                setFrozen,
                filterAndSetSelectionsByType,
            },
        });

        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._geoDataService = geoDataService;
        this._onSelectEventRegistry = new EventRegistry();
        this._onDeselectEventRegistry = new EventRegistry();
        this._onSelectionEventRegistry = new EventRegistry();
        this._onFrozenEventRegistry = new EventRegistry();
        this._cachedSelection = [];
        this._state.onChange(
            (newState, oldState, changedKeys) => {
                this._cachedSelection = newState.selection;
                const selected = diffSelection(newState.selection, oldState.selection);
                if (selected.length) {
                    this._onSelectEventRegistry.notifyListeners(selected);
                }

                const deselected = diffSelection(oldState.selection, newState.selection);
                if (deselected.length) {
                    this._onDeselectEventRegistry.notifyListeners(deselected);
                }
                this._onSelectionEventRegistry.notifyListeners({
                    selection: newState.selection,
                    selected,
                    deselected,
                });

                if (changedKeys.has('frozen')) {
                    this._onFrozenEventRegistry.notifyListeners(newState.frozen);
                }
                for (const selectedEntity of selected) {
                    const id = selectedEntity.id;
                    const featureLayer = this._kmlFeatureLayerCollection.get(id);
                    if (featureLayer) {
                        featureLayer.setSelected();
                    }
                }

                for (const deselectedEntity of deselected) {
                    const id = deselectedEntity.id;
                    const featureLayer = this._kmlFeatureLayerCollection.get(id);
                    if (featureLayer) {
                        featureLayer.setDeselected();
                    }
                }
            },
            {
                keys: ['selection', 'frozen'],
            },
        );
    }

    getSelectionImsObjects(connectionService: ConnectionService): Array<ImsSelection> {
        return this.getSelection().reduce((infoboxObjects, selection) => {
            const obj = this.getImsObjectForSelection(selection, connectionService);
            if (obj) {
                infoboxObjects.push(obj);
            }
            return infoboxObjects;
        }, []);
    }
    getImsObjectForSelection(selection: Selection, connectionService): ImsSelection {
        const collection = this._imsItemTypeRegistry.getTypeDetails(selection.type)?.collection;
        switch (selection.type) {
            case SelectionType.ENTITY: {
                return null;
            }
            case SelectionType.IP_DEVICE: {
                return getSelectedIPDevices([selection], collection)[0];
            }
            case SelectionType.ASSET: {
                return getSelectedAssets([selection], collection)[0];
            }
            case SelectionType.ZONE: {
                return getSelectedZones([selection], collection)[0];
            }
            case SelectionType.KML_FEATURE_LAYER: {
                return getSelectedKMLFeatureLayers([selection], collection)[0];
            }
            case SelectionType.CONNECTION: {
                return getSelectedConnectionEntities([selection], connectionService)[0];
            }
            case SelectionType.RF_TRANSMITTER: {
                return getSelectedRfTransmitters([selection], collection)[0];
            }
            case SelectionType.HEAT_MAP: {
                if (this._geoDataService) {
                    const { geoMapId } = parseSelectableFeatureId(selection.id as string);
                    const geoMap = this._geoDataService.get(geoMapId);
                    const typeDetails = this._imsItemTypeRegistry.getTypeDetails(geoMap.statType);
                    const schema = typeDetails?.statDefinition;
                    return {
                        id: selection.id as string,
                        imsType: selection.type,
                        name: !!schema.isDynamicStat
                            ? `${schema.display_name} Stat`
                            : schema.display_name,
                    };
                } else {
                    return {
                        id: selection.id as string,
                        imsType: selection.type,
                        name: 'Heatmap Selection',
                    };
                }
            }
            case SelectionType.SITE: {
                // There's currently no infobox for site, so return nothing if a site is selected
                return null;
            }
        }
        throw new Error(`Infobox could not handle selection for type ${selection.type}`);
    }

    getSelection(): Selection[] {
        // Use a cached version of the state to save cloning the selection on every request.
        // Cloning is redundant here anyway and it's only a shallow clone
        // and the cloned object only contains one key which could still be mutated.
        // return this._state.getState().selection;
        return this._cachedSelection;
    }

    /**
     * Get all of the selected IDs of the specified type
     * @param {SelectionType} type
     * @returns {any[]}
     */
    getSelectedIdsForType(type: SelectionType): Set<number> {
        return new Set(
            this.getSelection().reduce((result, s) => {
                if (s.type === type) {
                    result.push(s.id);
                }
                return result;
            }, []),
        );
    }

    isSelected(id: number | string, type: SelectionType) {
        try {
            const other = new Selection(id, type);
            return !!this.getSelection().find((s) => matchSelection(s, other));
        } catch (e) {
            return false;
        }
    }

    /**
     * Deselect everything with the given type
     * @param {SelectionType} type
     * @returns {Promise<Promise<any>>}
     */
    async deselectByType(type: SelectionType) {
        this._state.effects.filterAndSetSelectionsByType(type);
    }

    /**
     * Listen for changes for when something is selected
     * @param callback Called with a list of Selection items
     * @returns {function()} Destructor
     */
    onSelect(callback) {
        return this._onSelectEventRegistry.addEventListener(callback);
    }

    /**
     * Listen for changes for when something is deselected
     * @param callback Called with a list of Selection items that were deselected
     * @returns {function()} Destructor
     */
    onDeselect(callback) {
        return this._onDeselectEventRegistry.addEventListener(callback);
    }

    /**
     * Listen for changes for when the selection changes (selection or deselection)
     * @param callback Calls with a single object container a list of the current Selection objects
     * Also contains the newly selected Selections in 'selected' and the deselected Selections in 'deselected'
     * @returns {function()} Destructor
     */
    onSelectionChange(callback: (changes: OnSelectionChangedCallbackArgs) => void): () => void {
        return this._onSelectionEventRegistry.addEventListener(callback);
    }

    /**
     * Listener for when the 'frozen' state of selections change
     * @param callback Called with a single boolean
     * @returns {function()} Destructor
     */
    onFrozenChange(callback) {
        return this._onFrozenEventRegistry.addEventListener(callback);
    }

    /**
     * Set the current selection to an arbitrary number of Selection objects
     */
    setSelection(...selections: Array<Selection>): void {
        if (selections.length && selections.find((s) => !(s instanceof Selection))) {
            throw new Error('not all selections were valid Selection instances');
        }
        return this._state.effects.setSelectionFunc(selections);
    }

    /**
     * Select nothing
     */
    clearSelection() {
        return this.setSelection();
    }

    /**
     * Set the current frozen state
     */
    async setFrozen(frozen) {
        return this._state.effects.setFrozen(frozen);
    }
}
